import { useEffect, useState } from "react";
import { Printer, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatINR } from "@/lib/lamka";

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");
const addr = (a) => (typeof a === "string" ? a : a ? [a.area, a.landmark, a.city].filter(Boolean).join(", ") : "");

export const OrderStickerButton = ({ order }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || data) return;
    setError("");
    api.get(`/payment-proofs/${order.id}/order`)
      .then((r) => setData(r.data?.order || r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Order details aren't available yet."));
  }, [open, data, order.id]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`order-details-${order.id}`} className="flex items-center gap-2 border border-[#0F0F0F]/30 px-4 py-2 text-[10px] tracking-[0.25em] uppercase hover:border-[#0F0F0F] transition-colors">
          <FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> Details
        </button>
      </DialogTrigger>
      <DialogContent data-testid="order-sticker-dialog" className="rounded-none max-w-md bg-[#FBF9F5] p-0 border-[#0F0F0F] max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">Order details</DialogTitle>
        {error ? (
          <p data-testid="order-sticker-error" className="p-8 text-sm text-red-700">{error}</p>
        ) : !data ? (
          <p className="p-8 text-sm text-[#71717A]">Loading order details…</p>
        ) : (
          <Sticker d={data} order={order} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const Sticker = ({ d, order }) => {
  const buyer = d.buyer || {};
  const seller = d.seller || {};
  const listing = d.listing || {};
  const a = pick(d.address, d.delivery_address, d.shipping_address, buyer.address) || {};
  const qty = pick(d.quantity, order.quantity, 1);
  const unit = pick(d.unit_price, order.unit_price, listing.price, 0);
  const method = String(pick(d.method, order.method, ""));
  const rows = [
    ["Ship to", pick(a.full_name, buyer.name, d.buyer_name, order.buyer_name)],
    ["Phone", pick(a.phone, buyer.phone, d.buyer_phone)],
    ["Address", addr(a) || pick(d.address_text)],
  ];
  const from = [
    ["From", pick(seller.shop_name, seller.name, d.seller_name, order.seller_name)],
    ["Phone", pick(seller.phone, d.seller_phone)],
  ];
  return (
    <div id="lamka-sticker" data-testid="order-sticker" className="p-6 pt-12 sm:p-8 sm:pt-12">
      <div className="border-2 border-[#0F0F0F] bg-white">
        <div className="flex items-center justify-between px-5 py-3 bg-[#0F0F0F] text-white">
          <span className="font-display tracking-[0.18em] uppercase">Lamka <span className="text-[#C5A059]">Online</span></span>
          <span className="font-mono text-[10px] tracking-[0.2em]">#{String(pick(d.id, order.id)).slice(0, 8).toUpperCase()}</span>
        </div>
        <Section rows={rows} testid="sticker-ship-to" />
        <Section rows={from} testid="sticker-from" />
        <div className="px-5 py-4 space-y-1 text-sm" data-testid="sticker-item">
          <p className="font-display text-lg leading-snug break-words">{pick(listing.title, d.listing_title, order.listing_title, "Item")}</p>
          {(d.size || d.colour) && <p className="text-[#3f3f46]">{[d.size && `Size ${d.size}`, d.colour && `Colour ${d.colour}`].filter(Boolean).join(" · ")}</p>}
          <div className="flex justify-between font-mono text-xs pt-2">
            <span>Qty {qty} × {formatINR(unit)}</span>
            <span data-testid="sticker-total" className="font-semibold">{formatINR(pick(d.total, unit * qty))}</span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] pt-1">
            {method.toLowerCase().startsWith("cash") || method === "cod" ? "Cash on Delivery — collect on delivery" : "Paid via UPI"}
          </p>
        </div>
      </div>
      <button data-testid="sticker-print-button" onClick={() => window.print()} className="mt-5 w-full flex items-center justify-center gap-2 bg-[#0F0F0F] text-[#C5A059] py-3 text-xs tracking-[0.3em] uppercase print:hidden">
        <Printer className="h-4 w-4" strokeWidth={1.5} /> Print
      </button>
    </div>
  );
};

const Section = ({ rows, testid }) => (
  <dl data-testid={testid} className="px-5 py-4 border-b border-dashed border-[#0F0F0F]/40 space-y-1.5 text-sm">
    {rows.filter(([, v]) => v).map(([k, v]) => (
      <div key={k} className="grid grid-cols-[72px_1fr] gap-2">
        <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#71717A] pt-0.5">{k}</dt>
        <dd className="break-words">{v}</dd>
      </div>
    ))}
  </dl>
);
